import { ClearOutlined, SearchOutlined } from '@mui/icons-material'
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * a text field with a search icon and timer to update the query (ideal for debounced searchs)
 * @param props
 * @returns
 */
export const SearchTextField = (
  props: {
    currentQuery: string
    updateQuery: (query: string) => void
    /**
     * The time to wait before updating the query (milliseconds)
     */
    wait?: number
  } & Omit<TextFieldProps, 'InputProps'>,
) => {
  const { currentQuery, updateQuery, wait, ...rest } = props
  const [query, setQuery] = useState(props.currentQuery)
  const { t } = useTranslation()

  const updateTimer = useRef<NodeJS.Timeout>(undefined)

  useEffect(() => {
    !updateTimer.current && setQuery(currentQuery)
  }, [currentQuery])

  const handleChange = (e) => {
    clearTimeout(updateTimer.current)

    setQuery(e.target.value)

    updateTimer.current = setTimeout(
      () => updateQuery(e.target.value),
      wait ?? 500,
    )
  }

  return (
    <TextField
      {...(rest || {})}
      placeholder={t('common:search')}
      value={query || ''}
      onChange={handleChange}
      slotProps={{
        ...rest.slotProps,
        input: {
          ...rest.slotProps?.input,
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
          endAdornment: rest.slotProps?.input['endAdornment'] || (
            <InputAdornment position="end">
              {query && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery('')
                    updateQuery('')
                  }}
                  edge="end"
                  aria-label={t('common:clear')}
                  color="inherit"
                >
                  <ClearOutlined fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        },
      }}
      {...rest}
    />
  )
}
